import axios from "axios";
import { formatDate } from "date-fns";
import {instance} from "utils/axios.utils";
import { Functions } from "utils/imports.utils";
import { startSession } from "utils/session.utils";
import { setUser } from "utils/redux.utils";
import { IGoogleAuthRequest, IGoogleAuthResponse } from "interfaces/auth.interface";
import { GetUserInvoicesBody, GetUserInvoicesResponse } from "interfaces/invoice.interface";
import { ApiError } from "interfaces/ar_error.interface";
import { AR_REQUEST_TIMEOUT_MS } from "constants/ar_error.constant";
import { VisitCheckBody, VisitCheckResponse } from "interfaces/visit_check.interface";
import { ListSupportTicketsResponse } from "interfaces/support.interface";
// express-joi-validation answers a 400 with a plain-text body; every other server error is JSON { message }.
interface ServerErrorResponse {
  message: string;
  response: { data?: string | { message?: string } };
}

const serverMessage = (error: ServerErrorResponse): string =>
  (typeof error.response.data === "string" ? error.response.data : error.response.data?.message) || error.message;

// The public AR requests reject with the status as well as the message, so the screen can tell a missing
// project (404) from any other failure.
const toApiError = (error: { response?: { status?: number; data?: { message?: string } }; message?: string }): ApiError => ({
  message: error.response ? error.response.data?.message : error.message,
  status: error.response?.status,
});

const auth = {
  login: (data) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "auth/user_login";
      instance().post(url, data).then(res=>{
        resolve(res.data)
        startSession(res.data.token, res.data.data)
        setUser(res.data.data)
      }).catch(error=>{
        if(error.response){
          reject(serverMessage(error))
        }else{
          reject(error)
        }
      })
    })
    return promise
  },
  signup: (data) => {
    return new Promise((resolve, reject) => {
      let url = "auth/user_signup";
      instance()
        .post(url, data)
        .then((res) => {
          resolve(res.data);
          if (res.data.token && res.data.data) {
            startSession(res.data.token, res.data.data);
            setUser(res.data.data);
          }
        })
        .catch((error) => {
          console.error("Signup error:", error); // Log full error object
          if (error.response) {
            reject(serverMessage(error));
          } else {
            reject(error);
          }
        });
    });
  },

  google: (data: IGoogleAuthRequest) => {
    return new Promise<IGoogleAuthResponse>((resolve, reject) => {
      const url = "auth/google";
      instance()
        .post(url,data)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            // `desc` is what the server's central error handler sends for an unexpected failure.
            reject(error.response.data?.message || error.response.data?.desc || error.message);
          } else {
            reject(error);
          }
        });
    })
  },

  changePassword: (data) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "auth/change_password";
      instance().post(url, data).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },
  

  getUserProfile: () => {
    return new Promise((resolve, reject) => {
      let url = `auth/view_user`; // Replace :id with actual ID
      instance()
        .post(url)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },
  getUserInvoices: (body: GetUserInvoicesBody) => {
    return new Promise<GetUserInvoicesResponse>((resolve, reject) => {
      const url = 'invoice/get_many_invoice';
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  createProject: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "project/create_project";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },
  
  getManyProject: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "/project/get_many_project";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },

  getProject: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "project/get_project";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },

  getManyProjectData: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "project_data/get_many_project_data";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },

  editProjectData: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "project_data/edit_project_data";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },

  deleteProject: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "project/delete_project";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },

  editProject: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "project/edit_project";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },
  saveProjectData: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "project_data/save_project_data";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },
  
  publishProject: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "project/publish_project";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },


  passwordRecovery: (data) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "auth/forget_password";
      instance().post(url, data).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },
  resetPassword: (data) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "auth/reset_password";
      instance().post(url, data).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response && error.response.data){
          reject(serverMessage(error))
        }else{
          reject(error)
        }
      })
    })
    return promise
  },
  sendOtp: (data) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "auth/send_otp";
      instance().post(url, data).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response && error.response.data){
          reject(serverMessage(error))
        }else{
          reject(error)
        }
      })
    })
    return promise
  },
  verifyOtp: (data) => {
    let promise = new Promise((resolve, reject)=>{
      let url = "auth/verify_otp";
      instance().post(url, data).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response && error.response.data){
          reject(serverMessage(error))
        }else{
          reject(error)
        }
      })
    })
    return promise
  },

  // Revokes the token server-side. Best effort: the caller clears local state either way.
  logout: () => {
    return instance().post("auth/logout").then(res => res.data)
  },

  getUser: () => {
    let promise = new Promise((resolve, reject)=>{
      let url = "auth/view_user";
      instance().post(url).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },

  // editUser: (body) => {
  //   let promise = new Promise((resolve, reject)=>{
  //     let token:any = localStorage.getItem("token")
     
  //     let baseURL = Functions.getBaseURL()+"/api/v1/"
  //     let url = "auth/edit_user";
  //     const headers = {
  //       'Content-Type': `multipart/form-data`, // Add boundary for multipart
  //       'Authorization': token, // Replace with your actual token
  //     };
  //     axios.post(`${baseURL}${url}`,body, { headers })
  // .then(response => {
  //   console.log('Success:', response.data);
  // })
  // .catch(error => {
  //   console.error('Error:', error);
  // });
  //     // instance().post(url,body).then(res=>{
  //     //   resolve(res.data)
  //     // }).catch(error=>{
  //     //   if(error.response){
  //     //     reject(error.response.data?.message || error.message)
  //     //   }else{
  //     //     reject(error)
  //     //   }
  //     // })
  //   })
  //   return promise
  // },
  editUser: (body) => {
    let promise = new Promise((resolve, reject)=>{
      let token:any = localStorage.getItem("token")
      let baseURL = Functions.getBaseURL()+"/api/v1/"
      let url = "auth/edit_user";
      instance().post(url,body).then(res=>{
        resolve(res.data)
      }).catch(error=>{
        if(error.response){
          reject(error.response.data?.message || error.message)
        }else{
          reject(error)
        }
      })
    })
    return promise
  },

  uploadFile: (data) => {
    let promise = new Promise((resolve, reject) => {
      let url = 'auth/get_signed_url';
      instance()
        .post(url, data)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
    return promise;
  },

  createPaymentOrder: (data) => {
    const promise = new Promise((resolve, reject) => {
      let url = 'payment/create_payment_order';
      instance()
        .post(url, data)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response?.data?.message);
          } else {
            reject(error);
          }
        });
    });
    return promise;
  },

  getPriceQuote: (data: { project_id: string; target_ids: string[] }) => {
    const promise = new Promise((resolve, reject) => {
      const url = `payment/price_quote?project_id=${data.project_id}&target_ids=${data.target_ids.join(',')}`;
      instance()
        .get(url)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response?.data?.message);
          } else {
            reject(error);
          }
        });
    });
    return promise;
  },

  verifyPayment: (data) => {
    const promise = new Promise((resolve, reject) => {
      let url = 'payment/verify_payment';
      instance()
        .post(url, data)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response?.data?.message);
          } else {
            reject(error);
          }
        })
    })
    return promise;
  },

  // PAYMENT.md Phase 5 -- public, unauthenticated route, but `instance()` always attaches
  // whatever token localStorage has; the backend ignores it since `resolve` never checks auth.
  resolveTarget: (data: { target_id: string }) => {
    const promise = new Promise((resolve, reject) => {
      const url = `target/resolve/${data.target_id}`;
      instance()
        .get(url)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response?.data?.message);
          } else {
            reject(error);
          }
        });
    });
    return promise;
  },

  // Backend follow-up to Phase 5 -- public, unauthenticated route for the AR-scan path
  // (ar.screen.tsx). Anonymous QR-code scanners only ever have a project_id, never an auth
  // token, so this must not go through an auth-gated endpoint the way getManyProjectData does.
  resolveProjectData: (data: { project_id: string }) => {
    const promise = new Promise((resolve, reject) => {
      // project_id comes from a public link; encoding keeps any `/`, `..`, `?` or `#` inside this one path segment
      const url = `project_data/resolve/${encodeURIComponent(data.project_id)}`;
      instance()
        .get(url, { timeout: AR_REQUEST_TIMEOUT_MS })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(toApiError(error));
        });
    });
    return promise;
  },

  createRenewalOrder: (data: { target_id: string }) => {
    const promise = new Promise((resolve, reject) => {
      const url = 'payment/create_renewal_order';
      instance()
        .post(url, data)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response?.data?.message);
          } else {
            reject(error);
          }
        });
    });
    return promise;
  },

  updatePaymentStatus: (data) => {
    const promise = new Promise((resolve, reject) => {
      const url = 'payment/update_payment_status';
      instance()
        .post(url, data)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response?.data?.message);
          } else {
            reject(error);
          }
        })
    })
    return promise;
  },

  adminListUsers: (body) => {
    return new Promise((resolve, reject) => {
      let url = 'admin/users/list';
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminGetUserDetail: (userId: string) => {
    return new Promise((resolve, reject) => {
      let url = `admin/users/${userId}`;
      instance()
        .get(url)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminGetUserProjects: (userId: string, body) => {
    return new Promise((resolve, reject) => {
      let url = `admin/users/${userId}/projects`;
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminListProjects: (body) => {
    return new Promise((resolve, reject) => {
      let url = 'admin/projects/list';
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminBlockUser: (userId: string, body) => {
    return new Promise((resolve, reject) => {
      let url = `admin/users/${userId}/block`;
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminDeleteUser: (userId: string) => {
    return new Promise((resolve, reject) => {
      let url = `admin/users/${userId}/delete`;
      instance()
        .post(url, {})
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminBlockProject: (projectId: string, body) => {
    return new Promise((resolve, reject) => {
      let url = `admin/projects/${projectId}/block`;
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminDeleteProject: (projectId: string) => {
    return new Promise((resolve, reject) => {
      let url = `admin/projects/${projectId}/delete`;
      instance()
        .post(url, {})
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminGetProjectDetail: (projectId: string) => {
    return new Promise((resolve, reject) => {
      let url = `admin/projects/${projectId}`;
      instance()
        .get(url)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminListTickets: (body) => {
    return new Promise((resolve, reject) => {
      let url = 'admin/tickets/list';
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminGetTicketDetail: (ticketId: string) => {
    return new Promise((resolve, reject) => {
      let url = `admin/tickets/${ticketId}`;
      instance()
        .get(url)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminReplyTicket: (ticketId: string, body) => {
    return new Promise((resolve, reject) => {
      let url = `admin/tickets/${ticketId}/reply`;
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  createSupportTicket: (body: FormData) => {
    return new Promise((resolve, reject) => {
      let url = 'support/ticket/create';
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  listSupportTickets: (body) => {
    return new Promise<ListSupportTicketsResponse>((resolve, reject) => {
      let url = 'support/ticket/list';
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  getSupportTicket: (ticketId: string) => {
    return new Promise((resolve, reject) => {
      let url = `support/ticket/${ticketId}`;
      instance()
        .get(url)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  getSupportAttachment: (filename: string, ticketId: string) => {
    return new Promise((resolve, reject) => {
      let url = `support/attachment/${filename}?ticket_id=${ticketId}`;
      instance()
        .get(url, { responseType: 'blob' })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminSetTicketStatus: (ticketId: string, body) => {
    return new Promise((resolve, reject) => {
      let url = `admin/tickets/${ticketId}/status`;
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminGetTargetViews: (targetId: string) => {
    return new Promise((resolve, reject) => {
      let url = `admin/targets/${targetId}/views`;
      instance()
        .get(url)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminGetOverview: () => {
    return new Promise((resolve, reject) => {
      let url = 'admin/analytics/overview';
      instance()
        .post(url, {})
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  adminGetTopTargets: (body) => {
    return new Promise((resolve, reject) => {
      let url = 'admin/analytics/top_targets';
      instance()
        .post(url, body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          if (error.response) {
            reject(error.response.data?.message || error.message);
          } else {
            reject(error);
          }
        });
    });
  },

  checkVisit: (body: VisitCheckBody) => {
    return new Promise<VisitCheckResponse>((resolve, reject) => {
      const url = 'visit_tracker/check_visits';
      instance()
        .post(url, body, { timeout: AR_REQUEST_TIMEOUT_MS })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(toApiError(error));
        });
    });
  }
};

export default auth;